// Centralized i18n data loader for the Academy modules.
// Each getter returns the lang-specific dataset; falls back to ES.
//
// Why this file exists:
//   • The original ES data files (e.g. glosario-trading.js) used to be the
//     single source of truth. We auto-generated <name>.en.js and
//     <name>.sk.js siblings via Claude — same shape, translated strings.
//   • To avoid touching ~10 page components for every export, this module
//     consolidates the cross-locale lookup behind small helpers that take
//     `lang` ("es" | "en" | "sk").

// ── Glosario ──────────────────────────────────────────────────────
import {
  GLOSARIO as G_ES,
  GLOSARIO_CATEGORIAS as G_CATS_ES,
  CATEGORIA_COLORS,
} from "./glosario-trading";
import { GLOSARIO as G_EN, GLOSARIO_CATEGORIAS as G_CATS_EN } from "./glosario-trading.en";
import { GLOSARIO as G_SK, GLOSARIO_CATEGORIAS as G_CATS_SK } from "./glosario-trading.sk";
import { GLOSARIO as G_DE, GLOSARIO_CATEGORIAS as G_CATS_DE } from "./glosario-trading.de";
import { GLOSARIO as G_ZH, GLOSARIO_CATEGORIAS as G_CATS_ZH } from "./glosario-trading.zh";

const GLO = { es: G_ES, en: G_EN, sk: G_SK, de: G_DE, "zh-CN": G_ZH };
const GLO_CATS = { es: G_CATS_ES, en: G_CATS_EN, sk: G_CATS_SK, de: G_CATS_DE, "zh-CN": G_CATS_ZH };
// Map a (lang, term-id) lookup so detail/related-term links across locales still work.
const ID_TO_CAT_ES = Object.fromEntries(G_ES.map((t) => [t.id, t.categoria]));

export const getGlosario = (lang) => GLO[lang] || GLO.en || GLO.es;
export const getGlosarioCategorias = (lang) => GLO_CATS[lang] || GLO_CATS.en || GLO_CATS.es;
export const findTerminoLang = (id, lang) =>
  (GLO[lang] || GLO.en || GLO.es).find((t) => t.id === id);
// Category color lookup is keyed by the ES category name (e.g. "Smart Money");
// other-locale rows carry the translated category in `categoria`, so we
// translate back to ES first using the ID map for color resolution.
export const colorForCategoria = (term) =>
  CATEGORIA_COLORS[ID_TO_CAT_ES[term?.id] || term?.categoria] || "";

// ── Estrategias ──────────────────────────────────────────────────
import {
  ESTRATEGIAS as E_ES,
  ESTRATEGIA_TIPOS as ET_ES,
  ESTRATEGIA_NIVELES as EN_ES,
  TIPO_COLORS,
  NIVEL_COLORS,
} from "./estrategias-trading";
import { ESTRATEGIAS as E_EN, ESTRATEGIA_TIPOS as ET_EN, ESTRATEGIA_NIVELES as EN_EN } from "./estrategias-trading.en";
import { ESTRATEGIAS as E_SK, ESTRATEGIA_TIPOS as ET_SK, ESTRATEGIA_NIVELES as EN_SK } from "./estrategias-trading.sk";
import { ESTRATEGIAS as E_DE, ESTRATEGIA_TIPOS as ET_DE, ESTRATEGIA_NIVELES as EN_DE } from "./estrategias-trading.de";
import { ESTRATEGIAS as E_ZH, ESTRATEGIA_TIPOS as ET_ZH, ESTRATEGIA_NIVELES as EN_ZH } from "./estrategias-trading.zh";

const EST = { es: E_ES, en: E_EN, sk: E_SK, de: E_DE, "zh-CN": E_ZH };
const EST_TIPOS = { es: ET_ES, en: ET_EN, sk: ET_SK, de: ET_DE, "zh-CN": ET_ZH };
const EST_NIVELES = { es: EN_ES, en: EN_EN, sk: EN_SK, de: EN_DE, "zh-CN": EN_ZH };

export const getEstrategias = (lang) => EST[lang] || EST.en || EST.es;
export const getEstrategiaTipos = (lang) => EST_TIPOS[lang] || EST_TIPOS.en || EST_TIPOS.es;
export const getEstrategiaNiveles = (lang) => EST_NIVELES[lang] || EST_NIVELES.en || EST_NIVELES.es;
export const findEstrategiaLang = (id, lang) =>
  (EST[lang] || EST.en || EST.es).find((e) => e.id === id);
export { TIPO_COLORS, NIVEL_COLORS };

// ── Psicología ───────────────────────────────────────────────────
import { LECCIONES_PSICOLOGIA as P_ES } from "./psicologia-trading";
import { LECCIONES_PSICOLOGIA as P_EN } from "./psicologia-trading.en";
import { LECCIONES_PSICOLOGIA as P_SK } from "./psicologia-trading.sk";
import { LECCIONES_PSICOLOGIA as P_DE } from "./psicologia-trading.de";
import { LECCIONES_PSICOLOGIA as P_ZH } from "./psicologia-trading.zh";

const PSI = { es: P_ES, en: P_EN, sk: P_SK, de: P_DE, "zh-CN": P_ZH };
export const getLeccionesPsicologia = (lang) => PSI[lang] || PSI.en || PSI.es;
export const findLeccionLang = (id, lang) =>
  (PSI[lang] || PSI.en || PSI.es).find((l) => l.id === id);

// ── Métricas On-Chain ────────────────────────────────────────────
import {
  METRICAS_ONCHAIN as M_ES,
  COLOR_DOT,
  DIFICULTAD_COLOR,
  RECURSOS_EXTERNOS as R_ES,
} from "./metricas-onchain";
import { METRICAS_ONCHAIN as M_EN, RECURSOS_EXTERNOS as R_EN } from "./metricas-onchain.en";
import { METRICAS_ONCHAIN as M_SK, RECURSOS_EXTERNOS as R_SK } from "./metricas-onchain.sk";
import { METRICAS_ONCHAIN as M_DE, RECURSOS_EXTERNOS as R_DE } from "./metricas-onchain.de";
import { METRICAS_ONCHAIN as M_ZH, RECURSOS_EXTERNOS as R_ZH } from "./metricas-onchain.zh";

const MET = { es: M_ES, en: M_EN, sk: M_SK, de: M_DE, "zh-CN": M_ZH };
const REC = { es: R_ES, en: R_EN, sk: R_SK, de: R_DE, "zh-CN": R_ZH };
export const getMetricasOnchain = (lang) => MET[lang] || MET.en || MET.es;
export const getRecursosExternos = (lang) => REC[lang] || REC.en || REC.es;
export { COLOR_DOT, DIFICULTAD_COLOR };

// Cross-locale display labels for category / type / level keys that we keep
// in Spanish (because they're used as lookup keys for tailwind color tokens).
const CATEGORIA_LABEL = {
  es: { "Smart Money": "Smart Money", "Análisis Técnico": "Análisis Técnico", "Gestión de Riesgo": "Gestión de Riesgo", "Cripto": "Cripto", "DeFi": "DeFi", "Macro": "Macro", "Psicología": "Psicología" },
  en: { "Smart Money": "Smart Money", "Análisis Técnico": "Technical Analysis", "Gestión de Riesgo": "Risk Management", "Cripto": "Crypto", "DeFi": "DeFi", "Macro": "Macro", "Psicología": "Psychology" },
  sk: { "Smart Money": "Smart Money", "Análisis Técnico": "Technická analýza", "Gestión de Riesgo": "Risk manažment", "Cripto": "Krypto", "DeFi": "DeFi", "Macro": "Makro", "Psicología": "Psychológia" },
  de: { "Smart Money": "Smart Money", "Análisis Técnico": "Technische Analyse", "Gestión de Riesgo": "Risikomanagement", "Cripto": "Krypto", "DeFi": "DeFi", "Macro": "Makro", "Psicología": "Psychologie" },
  "zh-CN": { "Smart Money": "Smart Money", "Análisis Técnico": "技术分析", "Gestión de Riesgo": "风险管理", "Cripto": "加密货币", "DeFi": "DeFi", "Macro": "宏观", "Psicología": "心理学" },
};

const TIPO_ESTRATEGIA_LABEL = {
  es: { tendencia: "Tendencia", reversion: "Reversión", ruptura: "Ruptura", rango: "Rango", scalping: "Scalping" },
  en: { tendencia: "Trend", reversion: "Reversal", ruptura: "Breakout", rango: "Range", scalping: "Scalping" },
  sk: { tendencia: "Trend", reversion: "Reverz", ruptura: "Breakout", rango: "Range", scalping: "Scalping" },
  de: { tendencia: "Trend", reversion: "Umkehr", ruptura: "Ausbruch", rango: "Range", scalping: "Scalping" },
  "zh-CN": { tendencia: "趋势", reversion: "反转", ruptura: "突破", rango: "盘整", scalping: "剥头皮" },
};

const NIVEL_LABEL_MAP = {
  es: { basica: "Básica", intermedia: "Intermedia", avanzada: "Avanzada" },
  en: { basica: "Basic", intermedia: "Intermediate", avanzada: "Advanced" },
  sk: { basica: "Základná", intermedia: "Stredná", avanzada: "Pokročilá" },
  de: { basica: "Grundlagen", intermedia: "Fortgeschritten", avanzada: "Profi" },
  "zh-CN": { basica: "基础", intermedia: "中级", avanzada: "高级" },
};

export const labelCategoria = (cat, lang) => (CATEGORIA_LABEL[lang] || CATEGORIA_LABEL.en || CATEGORIA_LABEL.es)[cat] || cat;
export const labelTipoEstrategia = (tipo, lang) => (TIPO_ESTRATEGIA_LABEL[lang] || TIPO_ESTRATEGIA_LABEL.en || TIPO_ESTRATEGIA_LABEL.es)[tipo] || tipo;
export const labelNivelEstrategia = (nivel, lang) => (NIVEL_LABEL_MAP[lang] || NIVEL_LABEL_MAP.en || NIVEL_LABEL_MAP.es)[nivel] || nivel;

// ── Patrones gráficos ────────────────────────────────────────────
import {
  PATRONES as PA_ES,
  TIPOS,
  NIVELES,
  TIPO_LABEL as TL_ES,
  DIRECCION_LABEL as DL_ES,
  NIVEL_LABEL as NL_ES,
} from "./patronesGraficos";
import {
  PATRONES as PA_EN,
  TIPO_LABEL as TL_EN,
  DIRECCION_LABEL as DL_EN,
  NIVEL_LABEL as NL_EN,
} from "./patronesGraficos.en";
import {
  PATRONES as PA_SK,
  TIPO_LABEL as TL_SK,
  DIRECCION_LABEL as DL_SK,
  NIVEL_LABEL as NL_SK,
} from "./patronesGraficos.sk";
import {
  PATRONES as PA_DE,
  TIPO_LABEL as TL_DE,
  DIRECCION_LABEL as DL_DE,
  NIVEL_LABEL as NL_DE,
} from "./patronesGraficos.de";
import {
  PATRONES as PA_ZH,
  TIPO_LABEL as TL_ZH,
  DIRECCION_LABEL as DL_ZH,
  NIVEL_LABEL as NL_ZH,
} from "./patronesGraficos.zh";

const PAT = { es: PA_ES, en: PA_EN, sk: PA_SK, de: PA_DE, "zh-CN": PA_ZH };
const PAT_TL = { es: TL_ES, en: TL_EN, sk: TL_SK, de: TL_DE, "zh-CN": TL_ZH };
const PAT_DL = { es: DL_ES, en: DL_EN, sk: DL_SK, de: DL_DE, "zh-CN": DL_ZH };
const PAT_NL = { es: NL_ES, en: NL_EN, sk: NL_SK, de: NL_DE, "zh-CN": NL_ZH };

export const getPatrones = (lang) => PAT[lang] || PAT.en || PAT.es;
export const getTipoLabel = (lang) => PAT_TL[lang] || PAT_TL.en || PAT_TL.es;
export const getDireccionLabel = (lang) => PAT_DL[lang] || PAT_DL.en || PAT_DL.es;
export const getNivelLabel = (lang) => PAT_NL[lang] || PAT_NL.en || PAT_NL.es;
export const findPatronLang = (id, lang) =>
  (PAT[lang] || PAT.en || PAT.es).find((p) => p.id === id);
export const filtrarPatronesLang = (lang, tipo, nivel) =>
  (PAT[lang] || PAT.en || PAT.es).filter(
    (p) =>
      (tipo === "todos" || p.tipo === tipo) &&
      (nivel === "todos" || p.nivel === nivel)
  );
export { TIPOS, NIVELES };
